import React, { useEffect, useMemo, useState } from "react";
import { uploadImage } from "../../api/uploads";

export default function RealisationEditor({
  editingId,
  draft,
  setDraft,
  onSave,
  onCancel,
  saving,
  categories = [],
}) {
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  // ---- TAGS TEXT ----
  const [tagsText, setTagsText] = useState("");

  useEffect(() => {
    setTagsText((draft?.tags || []).join(", "));
  }, [editingId]);

  const parsedTags = useMemo(() => {
    return tagsText
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  }, [tagsText]);

  // ---- CATEGORIES (normalisées) ----
  const selectedCats = useMemo(() => {
    const arr = Array.isArray(draft?.categories) ? draft.categories : [];
    return Array.from(new Set(arr.map(String).map((s) => s.trim()).filter(Boolean)));
  }, [draft?.categories]);

  function toggleCategory(catKey) {
    const key = String(catKey).trim();

    setDraft((prev) => {
      const current = Array.isArray(prev.categories) ? prev.categories : [];
      const clean = Array.from(new Set(current.map(String).map((s) => s.trim()).filter(Boolean)));

      const has = clean.includes(key);
      const next = has ? clean.filter((x) => x !== key) : [...clean, key];

      return { ...prev, categories: next };
    });
  }

  // ---- HELPERS IMAGES ----
  const gallery = useMemo(() => {
    return Array.isArray(draft?.images) ? draft.images.filter((x) => x?.url) : [];
  }, [draft?.images]);

  function normalizeUploaded(up, fallbackAlt = "") {
    return {
      url: up?.url || "",
      publicId: up?.publicId || "",
      alt: fallbackAlt || "",
    };
  }

  // ---- UPLOAD COVER (1 fichier) ----
  async function onPickCover(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingCover(true);
    try {
      const up = await uploadImage(file);
      setDraft((d) => ({
        ...d,
        coverImage: {
          ...(d.coverImage || {}),
          url: up.url,
          publicId: up.publicId,
          alt: d.coverImage?.alt || d.title || "",
        },
      }));
    } catch (err) {
      alert(err?.message || "Erreur upload cover");
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  }

  // ---- UPLOAD GALLERY (plusieurs fichiers) ----
  async function onPickGallery(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadingGallery(true);
    try {
      const uploaded = [];

      // ✅ simple: upload 1 par 1 (fiable)
      for (const file of files) {
        const up = await uploadImage(file);
        uploaded.push(normalizeUploaded(up, draft.title || ""));
      }

      setDraft((d) => {
        const prev = Array.isArray(d.images) ? d.images : [];
        const nextImages = [...prev, ...uploaded].filter((x) => x?.url);

        // ✅ si pas de cover, on prend la 1ère image uploadée
        const hasCover = Boolean(d.coverImage?.url);
        const nextCover = hasCover
          ? d.coverImage
          : uploaded[0]
          ? { ...uploaded[0], alt: d.coverImage?.alt || d.title || uploaded[0].alt }
          : d.coverImage;

        return { ...d, images: nextImages, coverImage: nextCover };
      });
    } catch (err) {
      alert(err?.message || "Erreur upload galerie");
    } finally {
      setUploadingGallery(false);
      e.target.value = "";
    }
  }

  function removeGalleryImage(index) {
    setDraft((d) => {
      const prev = Array.isArray(d.images) ? [...d.images] : [];
      prev.splice(index, 1);
      return { ...d, images: prev };
    });
  }

  function setAsCover(img) {
    if (!img?.url) return;
    setDraft((d) => ({
      ...d,
      coverImage: {
        ...(d.coverImage || {}),
        url: img.url,
        publicId: img.publicId || "",
        alt: d.coverImage?.alt || img.alt || d.title || "",
      },
    }));
  }

  function handleSave() {
    // on envoie un payload propre
    const payload = {
      ...draft,
      categories: selectedCats,
      tags: Array.from(new Set(parsedTags)),
      images: gallery, // ✅ IMPORTANT: envoyer la galerie
      coverImage: draft.coverImage || { url: "", alt: "", publicId: "" },
    };
    onSave(payload);
  }

  const isBusy = saving || uploadingCover || uploadingGallery;

  return (
    <div className="real-editor">
      <div className="real-editor__head">
        <h3>{editingId === "NEW" ? "Ajouter une réalisation" : "Modifier la réalisation"}</h3>
      </div>

      <div className="real-editor__grid">
        <label>
          Titre
          <input
            value={draft.title || ""}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
          />
        </label>

        <label>
          Année
          <input
            type="number"
            value={draft.year || ""}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                year: e.target.value === "" ? "" : Number(e.target.value),
              }))
            }
          />
        </label>

        <label className="real-editor__full">
          Lien (optionnel)
          <input
            value={draft.link || ""}
            onChange={(e) => setDraft((d) => ({ ...d, link: e.target.value }))}
          />
        </label>

        {/* ✅ MULTI CATEGORIES */}
        <div className="real-editor__full">
          <div className="real-editor__sectionTitle">Catégories (une ou plusieurs)</div>

          <div className="real-catGrid">
            {categories.map((c) => {
              const checked = selectedCats.includes(c.key);

              return (
                <label key={c.key} className="real-catItem">
                  <input type="checkbox" checked={checked} onChange={() => toggleCategory(c.key)} />
                  <span className="real-catText">
                    <span className="real-catLabel">{c.label}</span>
                    {c.description ? <span className="real-catDesc">{c.description}</span> : null}
                  </span>
                </label>
              );
            })}
          </div>

          {selectedCats.length === 0 && (
            <div className="real-muted" style={{ marginTop: 8 }}>
              ⚠️ Choisis au moins une catégorie.
            </div>
          )}
        </div>

        <label className="real-editor__full">
          Description
          <textarea
            rows={4}
            value={draft.description || ""}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          />
        </label>

        <label className="real-editor__full">
          Tags (séparés par des virgules)
          <input
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            placeholder="ex: aluminium, voile, refit"
          />
          <div className="real-muted">
            {parsedTags.length ? (
              <>
                Tags détectés : <strong>{Array.from(new Set(parsedTags)).join(" • ")}</strong>
              </>
            ) : (
              "Aucun tag"
            )}
          </div>
        </label>

        {/* ===== COVER ===== */}
        <div className="real-editor__full">
          <div className="real-editor__imgRow">
            <div>
              <div>Image de couverture</div>
              <input type="file" accept="image/*" onChange={onPickCover} disabled={isBusy} />
              {uploadingCover && <div className="real-muted">Upload cover…</div>}
            </div>

            {draft.coverImage?.url ? (
              <img className="real-editor__preview" src={draft.coverImage.url} alt={draft.coverImage.alt || ""} />
            ) : (
              <div className="real-editor__previewEmpty">Aperçu</div>
            )}
          </div>

          <label>
            ALT (accessibilité)
            <input
              value={draft.coverImage?.alt || ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  coverImage: { ...(d.coverImage || {}), alt: e.target.value },
                }))
              }
            />
          </label>
        </div>

        {/* ===== GALLERY (MULTI) ===== */}
        <div className="real-editor__full">
          <div className="real-editor__sectionTitle">Galerie (plusieurs photos)</div>

          <input type="file" accept="image/*" multiple onChange={onPickGallery} disabled={isBusy} />
          {uploadingGallery && <div className="real-muted">Upload galerie…</div>}

          {gallery.length > 0 ? (
            <div className="real-galleryGrid">
              {gallery.map((img, idx) => {
                const isCover = draft.coverImage?.url && draft.coverImage.url === img.url;

                return (
                  <div key={`${img.url}-${idx}`} className="real-galleryItem">
                    <img src={img.url} alt={img.alt || ""} />

                    <div className="real-galleryActions">
                      <button
                        type="button"
                        className="real-btn real-btnSmall"
                        onClick={() => setAsCover(img)}
                        disabled={isBusy || isCover}
                      >
                        {isCover ? "Cover" : "Mettre en cover"}
                      </button>

                      <button
                        type="button"
                        className="real-btn real-btnSmall"
                        onClick={() => removeGalleryImage(idx)}
                        disabled={isBusy}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="real-muted" style={{ marginTop: 8 }}>
              Aucune photo dans la galerie pour l’instant.
            </div>
          )}
        </div>
      </div>

      <div className="real-editor__actions">
        <button className="real-btn" onClick={handleSave} disabled={isBusy}>
          Enregistrer
        </button>
        <button className="real-btn" onClick={onCancel} disabled={isBusy}>
          Annuler
        </button>
      </div>
    </div>
  );
}
